import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Download, ExternalLink } from 'lucide-react';

const EMPTY = { source: '', destination: '', type: 301, active: true, notes: '' };

const AdminRedirects = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editor, setEditor] = useState(null); // null=closed; {}=new; {...}=edit

  const load = () => {
    setLoading(true);
    api.get('/admin/redirects').then(r => setRows(r.data.redirects || [])).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (editor.id) {
        await api.patch(`/admin/redirects/${editor.id}`, {
          source: editor.source, destination: editor.destination, type: editor.type, active: editor.active, notes: editor.notes,
        });
      } else {
        await api.post('/admin/redirects', {
          source: editor.source, destination: editor.destination, type: editor.type, active: editor.active, notes: editor.notes,
        });
      }
      toast.success('Redirect saved');
      setEditor(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save redirect');
    }
  };

  const toggle = async (r) => {
    try { await api.patch(`/admin/redirects/${r.id}`, { active: !r.active }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete redirect ${r.source} → ${r.destination}?`)) return;
    try { await api.delete(`/admin/redirects/${r.id}`); toast.success('Deleted'); load(); }
    catch (e) { toast.error('Failed'); }
  };

  const exportUrl = async (format) => {
    try {
      const r = await api.get(`/admin/redirects/export?format=${format}`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = format === 'cloudflare' ? 'cloudflare_bulk_redirects.csv' : format === 'nginx' ? 'redirects.nginx.conf' : '_redirects';
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Export failed'); }
  };

  return (
    <div className='space-y-6' data-testid='admin-redirects-page'>
      <div className='flex flex-wrap items-center justify-between gap-3'>
        <div>
          <h1 className='text-2xl font-semibold tracking-tight'>Redirects</h1>
          <p className='text-sm text-muted-foreground'>Manage 301/302 redirects. These apply in-app immediately; export the rules to your CDN for true HTTP-level redirects.</p>
        </div>
        <div className='flex gap-2'>
          <Button variant='outline' size='sm' onClick={() => exportUrl('cloudflare')} data-testid='export-cloudflare'><Download className='h-4 w-4 mr-1' /> Cloudflare CSV</Button>
          <Button variant='outline' size='sm' onClick={() => exportUrl('nginx')} data-testid='export-nginx'><Download className='h-4 w-4 mr-1' /> nginx</Button>
          <Button size='sm' onClick={() => setEditor({ ...EMPTY })} data-testid='new-redirect-btn'><Plus className='h-4 w-4 mr-1' /> New redirect</Button>
        </div>
      </div>

      <Card className='p-0 overflow-hidden'>
        <table className='w-full text-sm'>
          <thead className='bg-secondary/50 text-left text-xs uppercase tracking-wider text-muted-foreground'>
            <tr>
              <th className='px-4 py-2'>Source</th>
              <th className='px-4 py-2'>Destination</th>
              <th className='px-4 py-2'>Type</th>
              <th className='px-4 py-2'>Active</th>
              <th className='px-4 py-2'>Hits</th>
              <th className='px-4 py-2'>Updated</th>
              <th className='px-4 py-2 text-right'>Actions</th>
            </tr>
          </thead>
          <tbody data-testid='redirects-table'>
            {loading ? (
              <tr><td colSpan={7} className='px-4 py-8 text-center text-muted-foreground'>Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={7} className='px-4 py-8 text-center text-muted-foreground'>No redirects yet.</td></tr>
            ) : rows.map(r => (
              <tr key={r.id} className='border-t' data-testid={`redirect-row-${r.id}`}>
                <td className='px-4 py-2 font-mono text-xs'>{r.source}</td>
                <td className='px-4 py-2 font-mono text-xs flex items-center gap-1'>{r.destination}{/^https?:/.test(r.destination) && <ExternalLink className='h-3 w-3' />}</td>
                <td className='px-4 py-2'><Badge variant={r.type === 301 ? 'default' : 'secondary'}>{r.type}</Badge></td>
                <td className='px-4 py-2'><Switch checked={!!r.active} onCheckedChange={() => toggle(r)} data-testid={`redirect-toggle-${r.id}`} /></td>
                <td className='px-4 py-2 text-muted-foreground'>{r.hits || 0}</td>
                <td className='px-4 py-2 text-muted-foreground text-xs'>{(r.updated_at || '').slice(0, 10)}</td>
                <td className='px-4 py-2 text-right whitespace-nowrap'>
                  <Button variant='ghost' size='icon' onClick={() => setEditor({ ...r })} data-testid={`redirect-edit-${r.id}`}><Pencil className='h-4 w-4' /></Button>
                  <Button variant='ghost' size='icon' onClick={() => remove(r)} data-testid={`redirect-delete-${r.id}`}><Trash2 className='h-4 w-4 text-destructive' /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Dialog open={!!editor} onOpenChange={(o) => !o && setEditor(null)}>
        <DialogContent data-testid='redirect-editor'>
          <DialogHeader><DialogTitle>{editor?.id ? 'Edit redirect' : 'New redirect'}</DialogTitle></DialogHeader>
          {editor && (
            <div className='space-y-4'>
              <div>
                <Label htmlFor='r-source'>Source / Old URL</Label>
                <Input id='r-source' placeholder='/all-categories' value={editor.source} onChange={e => setEditor({ ...editor, source: e.target.value })} data-testid='redirect-source-input' />
                <p className='text-[11px] text-muted-foreground mt-1'>Path only, e.g. <code>/blogs</code>. Query strings and trailing slashes are normalized.</p>
              </div>
              <div>
                <Label htmlFor='r-dest'>Destination / New URL</Label>
                <Input id='r-dest' placeholder='/categories' value={editor.destination} onChange={e => setEditor({ ...editor, destination: e.target.value })} data-testid='redirect-destination-input' />
              </div>
              <div className='flex items-center gap-6'>
                <div className='flex-1'>
                  <Label>Type</Label>
                  <Select value={String(editor.type)} onValueChange={v => setEditor({ ...editor, type: parseInt(v) })}>
                    <SelectTrigger data-testid='redirect-type-select'><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value='301'>301 — Permanent</SelectItem>
                      <SelectItem value='302'>302 — Temporary</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className='flex items-center gap-2 pt-5'>
                  <Switch checked={!!editor.active} onCheckedChange={v => setEditor({ ...editor, active: v })} data-testid='redirect-active-switch' />
                  <Label>Active</Label>
                </div>
              </div>
              <div>
                <Label htmlFor='r-notes'>Notes (optional)</Label>
                <Input id='r-notes' value={editor.notes || ''} onChange={e => setEditor({ ...editor, notes: e.target.value })} data-testid='redirect-notes-input' />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant='outline' onClick={() => setEditor(null)}>Cancel</Button>
            <Button onClick={save} data-testid='redirect-save-btn'>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminRedirects;
